/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import { JiraFilterAri } from '@atlassian/ari/jira/filter';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	isCustomFilter,
	isFilterId,
} from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import {
	getMaxIssuesPerPage,
	ISSUE_SEARCH_NAMESPACE,
	MAX_AMOUNT_OF_COLUMNS,
	NATURAL_LANGUAGE_TO_JQL,
	NIN_GLOBAL_OPERATION_SCOPE,
	NIN_PROJECT_OPERATION_SCOPE,
} from '@atlassian/jira-issue-navigator/src/common/constants.tsx';
import { convertFilterIdToIssueNavigatorId } from '@atlassian/jira-issue-navigator/src/common/utils/index.tsx';
import { getOperationScope } from '@atlassian/jira-issue-table-common/src/utils/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { RELAY_RESOURCE_TYPE } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { createResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { createViewAri } from '@atlassian/jira-issue-navigator-view-ari/src/index.tsx';
import JQLBuilderWithAIFilterRefinementQuery, {
	type JQLBuilderWithAIFilterRefinementQuery as JQLBuilderWithAIFilterRefinementQueryType,
} from '@atlassian/jira-jql-builder-with-ai/src/ui/filters/filter-refinement/__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql';
import HydrationRelayQuery, {
	type useHydrationQueryLoaderQuery as HydrationRelayQueryType,
	type useHydrationQueryLoaderQuery$variables,
} from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import HeaderFilterRelayQuery, {
	type main_FilterQuery as FilterRelayQueryType,
} from '@atlassian/jira-issue-navigator/src/__generated__/main_FilterQuery.graphql';
import IssueNavigatorRelayQuery, {
	type JiraAtlassianIntelligenceFeatureEnum,
	type main_IssueNavigatorQuery as IssueNavigatorRelayQueryType,
	type main_IssueNavigatorQuery$variables,
	type JiraIssueSearchOperationScope,
	type JiraIssueSearchScope,
} from '@atlassian/jira-issue-navigator/src/__generated__/main_IssueNavigatorQuery.graphql';
import {
	RESOURCE_TYPE_ISSUE_NAVIGATOR_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_HYDRATION_RELAY,
	RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_REFINEMENT_RELAY,
} from './constants.tsx';
import {
	getHydrateJqlInput,
	getIssueSearchInput,
	getJqlOrFilterId,
	getParams,
	isChromelessRoute,
} from './controllers/get-data/utils.tsx';

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorRelayResource = createResource<
	PreloadedQuery<IssueNavigatorRelayQueryType>
>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_RELAY}`,
	getKey: ({
		match: {
			params: { projectKey },
		},
		query: { filter },
	}) => {
		if (projectKey) {
			return `issue-navigator-relay-${projectKey}`;
		}

		if (!fg('nin_prevent_route_refresh_on_filter_change')) {
			const filterId = Boolean(filter) && isFilterId(filter) ? filter : null;

			if (filterId) {
				return `issue-navigator-relay-${filterId}`;
			}
		}

		return 'issue-navigator-relay';
	},
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}

		const {
			projectKey,
			issueKey,
			jql: jqlParam,
			defaultJql,
			filter,
			groupBy,
			hideDone,
		} = getParams(routerContext);

		const issueSearchInput = fg('jira_nin_last_used_jql')
			? getIssueSearchInput(defaultJql, jqlParam, filter, projectKey)
			: getJqlOrFilterId(defaultJql, jqlParam, filter, projectKey);

		const maxIssuesPerPage = getMaxIssuesPerPage();
		const chromelessRoute = isChromelessRoute(routerContext);
		let viewId;
		let shouldQueryConditionalFormatting = Boolean(projectKey);
		let isIFCEnabled = Boolean(projectKey);

		const filterId = Boolean(filter) && isCustomFilter(filter) ? filter : null;
		if (chromelessRoute) {
			// Disable conditional formatting for chromeless (i.e. embed) routes when saved views is disabled
			shouldQueryConditionalFormatting = fg('saved_list_view_technical_changes');
			// Disable IFC for chromeless (i.e. embed) routes
			isIFCEnabled = false;
			// Explicitly load list view for chromeless (i.e. embed) routes
			viewId = filterId ? convertFilterIdToIssueNavigatorId(filterId) : 'list_default';
		}

		let staticViewInput;
		if (projectKey === undefined || projectKey === null) {
			staticViewInput = {
				isHierarchyEnabled: false,
				isGroupingEnabled: false,
				isHideDoneEnabled: false,
			};
		} else if (hideDone != null) {
			staticViewInput = { isHideDoneEnabled: hideDone };
		}

		const viewConfigInput = staticViewInput
			? { staticViewInput }
			: { viewInput: { namespace: ISSUE_SEARCH_NAMESPACE } };

		let viewQueryInput: main_IssueNavigatorQuery$variables['viewQueryInput'];

		if (fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')) {
			const itemIdSlug = routerContext.match.params.navigationItemId;
			const itemId = itemIdSlug ? `list/${itemIdSlug}` : 'list';

			viewQueryInput =
				projectKey == null
					? {
							viewAri: createViewAri({
								activationId: tenantContext.activationId,
								cloudId: tenantContext.cloudId,
							}),
						}
					: {
							projectKeyAndItemIdQuery: {
								cloudId: tenantContext.cloudId,
								projectKey,
								itemId,
							},
						};
		}

		let operationScope: JiraIssueSearchOperationScope;

		if (isIFCEnabled && expVal('jira_inline_field_config', 'isInlineFieldConfigEnabled', false)) {
			operationScope = getOperationScope(projectKey);
		} else if (projectKey) {
			operationScope = NIN_PROJECT_OPERATION_SCOPE;
		} else {
			operationScope = NIN_GLOBAL_OPERATION_SCOPE;
		}

		const scope: JiraIssueSearchScope = {
			operationScope,
		};
		// Behavioural change from IFC. Previously we omitted the project key in embed view - which
		// means default global field sets would be loaded. Now we load field sets configured to the
		// project in both project and embed routes
		if (fg('jira_nin_last_used_jql')) {
			scope.projectKey = projectKey ?? null;
		} else if (
			isIFCEnabled &&
			expVal('jira_inline_field_config', 'isInlineFieldConfigEnabled', false)
		) {
			scope.projectKey = projectKey;
		}

		let fieldSetsScope = null;
		if (fg('jira_nin_last_used_jql')) {
			fieldSetsScope = {
				operationScope,
				projectKey: projectKey ?? null,
			};
		} else if (
			isIFCEnabled &&
			expVal('jira_inline_field_config', 'isInlineFieldConfigEnabled', false)
		) {
			fieldSetsScope = {
				operationScope,
				projectKey,
			};
		}

		const variables: main_IssueNavigatorQuery$variables = {
			cloudId: tenantContext.cloudId,
			first: issueKey == null ? maxIssuesPerPage : null,
			issueSearchInput,
			namespace: ISSUE_SEARCH_NAMESPACE,
			fieldSetsInput: {
				viewInput: {
					namespace: ISSUE_SEARCH_NAMESPACE,
					filterId,
					viewId,
				},
			},
			after: null,
			options: { issueKey },
			filterId,
			amountOfColumns: MAX_AMOUNT_OF_COLUMNS,
			shouldQueryHasChildren:
				projectKey !== null &&
				projectKey !== undefined &&
				expVal('jsc_m2_hierarchy', 'isHierarchyEnabled', false),
			...(fg('jsc_nin_operation_scope_fe') && {
				scope,
			}),
			...((expVal('jsc_m2_hierarchy', 'isHierarchyEnabled', false) ||
				fg('jsc_hide_done_view_config_fix')) && {
				viewConfigInput,
			}),
			staticViewInput,
			...((fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')) && {
				viewQueryInput,
				settings: {
					groupBy,
					hideDone,
					jql: jqlParam,
				},
			}),
			atlassianIntelligenceProductFeature:
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				NATURAL_LANGUAGE_TO_JQL as JiraAtlassianIntelligenceFeatureEnum,
			projectKey: projectKey ?? '',
			shouldQueryProject: Boolean(projectKey),
			shouldQueryConditionalFormatting:
				shouldQueryConditionalFormatting && fg('jira_list_conditional_formatting'),
			shouldQueryConditionalFormattingFromSavedView:
				// eslint-disable-next-line jira/ff/no-preconditioning
				shouldQueryConditionalFormatting &&
				fg('jira_list_conditional_formatting') &&
				fg('saved_list_view_technical_changes'),
			groupBy: groupBy ?? null,
			fieldSetsScope,
			viewId,
		};

		const queryReference = loadQuery<IssueNavigatorRelayQueryType>(
			getRelayEnvironment(),
			IssueNavigatorRelayQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorFilterRelayResource = createResource<
	PreloadedQuery<FilterRelayQueryType>
>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_RELAY}`,
	getKey: () => 'issue-navigator-filter-relay',
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}

		const { filter } = getParams(routerContext);
		const includeFilter = Boolean(filter) && isFilterId(filter);

		const variables = {
			includeFilter,
			filterAri: includeFilter
				? createAri({
						resourceOwner: 'jira',
						cloudId: tenantContext.cloudId,
						resourceType: 'filter',
						resourceId: filter,
						activationId: tenantContext.activationId,
					})
				: '',
		};
		const options = { fetchPolicy: includeFilter ? 'network-only' : 'store-only' } as const;

		const queryReference = loadQuery(
			getRelayEnvironment(),
			HeaderFilterRelayQuery,
			variables,
			options,
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorHydrationRelayResource = createResource<
	PreloadedQuery<HydrationRelayQueryType>
>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_HYDRATION_RELAY}`,
	getKey: () => 'issue-navigator-hydration-relay',
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}

		const { projectKey, jql: jqlParam, defaultJql, filter } = getParams(routerContext);

		const isProjectNIN = Boolean(projectKey);

		if (isProjectNIN && expVal('list_lovability_improving_filters_exp', 'isEnabled', false)) {
			return null;
		}

		let variables: useHydrationQueryLoaderQuery$variables;
		let itemIdSlug;
		let itemId;
		if (fg('jira_nin_last_used_jql')) {
			if (fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')) {
				itemIdSlug = routerContext.match.params.navigationItemId;
				itemId = itemIdSlug ? `list/${itemIdSlug}` : 'list';
			}

			const hydrateJqlInput = getHydrateJqlInput({
				activationId: tenantContext.activationId,
				cloudId: tenantContext.cloudId,
				defaultJql,
				jqlParam,
				filter,
				projectKey,
				...(fg('saved_list_view_technical_changes') && {
					itemId,
				}),
			});
			variables = {
				accountId: tenantContext.atlassianAccountId ?? '',
				cloudId: tenantContext.cloudId,
				includeUser: true,
				includeJira: true,
				isFilter: false,
				jql: '',
				filterAri: '',
				viewContext: null,
				input: hydrateJqlInput,
				isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
			};
		} else {
			const jqlOrFilterId = getJqlOrFilterId(defaultJql, jqlParam, filter, projectKey);

			variables = {
				accountId: tenantContext.atlassianAccountId ?? '',
				cloudId: tenantContext.cloudId,
				includeUser: true,
				includeJira: true,
				isFilter: 'filterId' in jqlOrFilterId,
				jql: ('jql' in jqlOrFilterId && jqlOrFilterId.jql) || '',
				filterAri:
					('filterId' in jqlOrFilterId &&
						JiraFilterAri.create({
							siteId: tenantContext.cloudId,
							filterId: filter,
							activationId: tenantContext.activationId,
						}).toString()) ||
					'',
				viewContext: null,
				input: null,
				isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
			};
		}

		const queryReference = loadQuery<HydrationRelayQueryType>(
			getRelayEnvironment(),
			HydrationRelayQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});

/**
 * We are using `createResource` instead of `createRelayResource` for two main reasons:
 * 1. The `getKey` provided by `createRelayResource` triggers this resource to reload every time a URL parameters change
 *    causes the query variables to change, preventing us from using our own custom reload hooks that are further down
 *    in Issue Navigator. This results in a full-page loading state any time one of the page params changes.
 * 2. Disabling this resource for SSR+Anonymous users (see comment below in `getData`)
 */
export const issueNavigatorHydrationRelayResourceBrowserOnly = createResource<
	PreloadedQuery<HydrationRelayQueryType>
>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_HYDRATION_RELAY}_BROWSER_ONLY`,
	getKey: () => 'issue-navigator-hydration-relay-browser-only',
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous+users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}

		const { projectKey, jql: jqlParam, defaultJql, filter } = getParams(routerContext);

		let variables: useHydrationQueryLoaderQuery$variables;
		let itemIdSlug;
		let itemId;
		if (fg('jira_nin_last_used_jql')) {
			if (fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')) {
				itemIdSlug = routerContext.match.params.navigationItemId;
				itemId = itemIdSlug ? `list/${itemIdSlug}` : 'list';
			}

			const hydrateJqlInput = getHydrateJqlInput({
				activationId: tenantContext.activationId,
				cloudId: tenantContext.cloudId,
				defaultJql,
				jqlParam,
				filter,
				projectKey,
				...(fg('saved_list_view_technical_changes') && {
					itemId,
				}),
			});
			variables = {
				accountId: tenantContext.atlassianAccountId ?? '',
				cloudId: tenantContext.cloudId,
				includeUser: true,
				includeJira: true,
				// Remove argument and query variable on jira_nin_last_used_jql cleanup
				isFilter: false,
				// Remove argument and query variable on jira_nin_last_used_jql cleanup
				jql: '',
				// Remove argument and query variable on jira_nin_last_used_jql cleanup
				filterAri: '',
				viewContext: null,
				input: hydrateJqlInput,
				isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
			};
		} else {
			const jqlOrFilterId = getJqlOrFilterId(defaultJql, jqlParam, filter, projectKey);

			variables = {
				accountId: tenantContext.atlassianAccountId ?? '',
				cloudId: tenantContext.cloudId,
				includeUser: true,
				includeJira: true,
				isFilter: 'filterId' in jqlOrFilterId,
				jql: ('jql' in jqlOrFilterId && jqlOrFilterId.jql) || '',
				filterAri:
					('filterId' in jqlOrFilterId &&
						JiraFilterAri.create({
							siteId: tenantContext.cloudId,
							filterId: filter,
							activationId: tenantContext.activationId,
						}).toString()) ||
					'',
				viewContext: null,
				input: null,
				isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
			};
		}

		const queryReference = loadQuery<HydrationRelayQueryType>(
			getRelayEnvironment(),
			HydrationRelayQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
	isBrowserOnly: true,
});

export const issueNavigatorFilterRefinementRelayResource = createResource<
	PreloadedQuery<JQLBuilderWithAIFilterRefinementQueryType>
>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_ISSUE_NAVIGATOR_FILTER_REFINEMENT_RELAY}`,
	getKey: () => 'issue-navigator-filter-refinement-relay',
	// @ts-expect-error - might return null
	getData: async (routerContext, { tenantContext }) => {
		// SSR/AGG does not currently work for anonymous users and returns a 403. We should enable anonymous SSR once
		// the underlying auth issues have been fixed. See more details here https://hello.atlassian.net/wiki/spaces/~hkim/pages/1853841608/Why+are+Jira+SSR+requests+to+AGG+not+working+for+anonymous users
		if (__SERVER__ && tenantContext.isAnonymous) {
			return null;
		}

		const { projectKey } = getParams(routerContext);

		const isProjectNIN = Boolean(projectKey);

		if (!isProjectNIN || !expVal('list_lovability_improving_filters_exp', 'isEnabled', false)) {
			return null;
		}

		const variables = {
			cloudId: tenantContext.cloudId,
			isFilterRefinementEnabled: true,
			scope: {
				project: {
					projectKey,
				},
			},
		};

		const queryReference = loadQuery<JQLBuilderWithAIFilterRefinementQueryType>(
			getRelayEnvironment(),
			JQLBuilderWithAIFilterRefinementQuery,
			variables,
			{
				fetchPolicy: 'network-only',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});
